import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../../api";
import {
  DestinationSystem,
  EntityType,
  JobStatus,
  MigrationRunStatus,
  TargetEnvironment,
  type EntityRecordViewCanonicalEntities,
  type MyPayEnvironment,
} from "../../generated/core-api";
// Deep-imported (not the `../../onboarding` or `../../onboarding/hooks`
// barrel): mirrors the established precedent in `useOnboardingWorkload.ts`'s
// own barrel-avoidance comment, followed again by
// `useWorkPatternImportWorkloadsManager.ts` (Step 8) — going through either
// barrel drags in a much wider surface (screens/layouts, or the whole hooks
// module) that has previously broken `vi.mock` partial-mocking elsewhere.
import { useOnboardingWorkload } from "../../onboarding/hooks/useOnboardingWorkload";
import { useOnboardingImport } from "../../onboarding/hooks/useOnboardingImport";
import { useOnboardingValidation } from "../../onboarding/hooks/useOnboardingValidation";
import { useOnboardingMigrationRun } from "../../onboarding/hooks/useOnboardingMigrationRun";
import { resolveIfMatchSourceVersion } from "../../onboarding/utils/resolveIfMatchSourceVersion";
import type { ComponentResolutionMap } from "../utils/buildImportRows";
import type { ImportResult } from "../types";
import { usePatchPayComponentAmbiguity } from "./usePatchPayComponentAmbiguity";
import type { HeldPayComponentRow } from "../utils/payComponentEntityPatch";
import {
  mapRunViewToImportResult,
  mapValidationReportToImportResult,
} from "../utils/payComponentWorkloadsResultMapper";
import { workloadHasSucceededPayComponentsRun } from "../utils/payComponentRunEligibility";

/**
 * LADR-039 (#440) Step 12 — the Slice 5 **workloads** REST replacement for
 * `useWorkflowCommit` (Track 1, `invokeAgent`/Temporal `pay_component_import`
 * workflow). Wired into `WizardProviderWorkloads` / `ReviewScreen` when
 * `EnableWorkloadsCutover` is on (Step 13).
 *
 * Pipeline (see `.plans/440-feature-ladr-039-portal-cutover.md` Step 12 and
 * `.plans/ladr-039-contract-mapping.md`'s pay-components vertical for the
 * authoritative shape mapping):
 *
 *   import (multipart file) --> GET held rows --> optional PATCH (W3b,
 *   ambiguity stamp) --> validate --> run
 *
 * **Single combined `commit()`, unlike Step 8's separate `validate()`/
 * `commit()`.** The work-patterns manager exposes two public entry points
 * because its UX shows the user a validation report and waits for an
 * explicit "Confirm Import" before pushing. The pay-components wizard
 * already has that confirmation gate earlier in the flow — the Christmas-
 * direct preflight (`useImportPayComponents`, W3c, kept unchanged) runs a
 * dry-run and the user reviews it on `ReviewScreen` *before*
 * `WizardProvider.commitImport` (this hook's caller under the cutover flag)
 * is ever invoked. So there is no second "review, then confirm" step to
 * preserve here: a single `commit()` call runs the whole hold-through-run
 * pipeline, matching `useWorkflowCommit.commit()`'s existing single-call
 * semantics that `WizardProvider.commitImport` already relies on.
 *
 * **Fire-and-forget, unlike `useWorkflowCommit.commit(): Promise<ImportResult>`.**
 * This hook's `commit()` does not return a promise — it mirrors the
 * reactive invoke-then-poll pattern of the composed `useOnboardingImport` /
 * `useOnboardingValidation` / `useOnboardingMigrationRun` hooks (and Step
 * 8's manager). Bridging this shape difference against
 * `WizardProvider.commitImport`'s current `await commit(...)` call is Step
 * 13 scope, not this hook's.
 *
 * **No chunking, no caller-supplied idempotency key.** Track 1's
 * `COMMIT_CHUNK_SIZE`-based chunk loop, `AbortController` threading, and
 * per-chunk resumability/aggregation (`commitProgress.ts`,
 * `creditReplayedAssignmentExists`) have no Track 2 equivalent: one durable
 * hold (`POST …/imports`, capped by the uploaded file's own server-side row
 * limit) plus one `POST …/runs` (`EntityRecordPatch.canonical_entities` is
 * capped at `@maxItems 500`) replaces the whole chunked commit loop, and
 * `MigrationRunCreate` has no `correlation_id` field to reuse across resume
 * attempts (see `useOnboardingMigrationRun.ts`'s own doc comment and
 * `.plans/ladr-039-contract-mapping.md`'s residual-mismatches list). A lost
 * session (e.g. a page reload mid-pipeline) is not resumed — a fresh
 * `commit()` call starts an entirely new pipeline, matching the Step 8
 * work-patterns manager's precedent (it doesn't reproduce Track 1's
 * `idempotency_key` reuse either).
 *
 * **No post-run per-row detail; run-time errors are manager-level, not
 * structural.** See `payComponentWorkloadsResultMapper.ts`'s doc comment for
 * the `RunView`/`ValidationReport` residuals (no `warnings` field on
 * `ImportResult`, no per-row `RunView` outcome).
 *
 * **ETag/If-Match residual** — inherited from
 * `usePatchPayComponentAmbiguity`/`patchOnboardingEntityRecord`; not
 * re-documented here (see that hook's module doc comment).
 */

const DESTINATION_SYSTEM = DestinationSystem.christmas;
const PAY_COMPONENTS_ENTITY_TYPE = EntityType.pay_components;
/** Pay-component Track 2 commits use Sandbox `test` pushes (LADR-039 C1). */
const PAY_COMPONENTS_TARGET_ENVIRONMENT = TargetEnvironment.test;

export interface PayComponentImportWorkloadsCommitArgs {
  /** The CSV file to durably hold via `createOnboardingImport`. */
  file: File;
  companyCode: string;
  mypayEnvironment: MyPayEnvironment;
  providerId?: string;
  /**
   * Component name -> chosen `companyComponentId`, from
   * `WizardState.componentResolutions` (Map Columns, W3b). When empty/absent,
   * the PATCH stage is skipped entirely and the rows held by the import are
   * validated/run as-is.
   */
  componentResolutions?: ComponentResolutionMap;
}

export interface UsePayComponentImportWorkloadsManagerResult {
  /** Kicks off the whole import -> read -> optional patch -> validate -> run pipeline. Fire-and-forget (see module doc comment). */
  commit: (args: PayComponentImportWorkloadsCommitArgs) => void;
  /**
   * The latest structured outcome: the post-run result once the run
   * succeeds, else the pre-run validation result once validation completes
   * (which may be `status: "invalid"` — see
   * `mapValidationReportToImportResult`), else `null` while the pipeline is
   * still in earlier stages.
   */
  result: ImportResult | null;
  isCommitting: boolean;
  managerError: string | null;
  reset: () => void;
}

/**
 * `EntityRecordView.canonical_entities` is a union of the three onboarding
 * verticals' row shapes (`CompanyShellRow | WorkPatternRow | ImportRow`) —
 * always requesting `entity_type: pay_components` guarantees every element
 * of the array is actually the generated `ImportRow` variant (aliased
 * `HeldPayComponentRow` by `payComponentEntityPatch.ts`), which justifies
 * this narrowing assertion (root AGENTS.md "Strong typing: no `any` without
 * justification").
 */
function asHeldRows(
  canonicalEntities: EntityRecordViewCanonicalEntities | undefined,
): HeldPayComponentRow[] {
  return (canonicalEntities ?? []) as HeldPayComponentRow[];
}

/**
 * Orchestrates the pay-components hold-through-run flow via the Slice 5
 * workloads REST surface. See the module-level doc comment above for the
 * pipeline shape and the residuals it does not attempt to reproduce.
 */
export function usePayComponentImportWorkloadsManager(): UsePayComponentImportWorkloadsManagerResult {
  const [companyCode, setCompanyCode] = useState<string | null>(null);
  const [guardError, setGuardError] = useState<string | null>(null);
  const [isPipelineActive, setIsPipelineActive] = useState(false);

  /** Synchronous ref mirrors — prevent React state-batching races (same rationale as Step 8). */
  const companyCodeRef = useRef<string | null>(null);
  const mypayEnvironmentRef = useRef<MyPayEnvironment | null>(null);
  const providerIdRef = useRef<string | undefined>(undefined);
  const componentResolutionsRef = useRef<ComponentResolutionMap>({});
  const fileRef = useRef<File | null>(null);
  /**
   * Gates the async if_match lookup + import kickoff (LADR-039 R-S1-1) so the
   * driver effect cannot fire concurrent create-import calls while awaiting.
   */
  const importKickoffInFlightRef = useRef(false);
  /**
   * After validation succeeds, `null` until we know whether D-2 blocks a second
   * `POST …/runs`; `false` means skip push (re-import / #212 validate-only).
   */
  const [runPushAllowed, setRunPushAllowed] = useState<boolean | null>(null);
  const runEligibilityCheckInFlightRef = useRef(false);

  const workload = useOnboardingWorkload({
    companyCode: companyCode ?? undefined,
    destinationSystem: DESTINATION_SYSTEM,
  });
  const importState = useOnboardingImport();
  const validationState = useOnboardingValidation();
  const runState = useOnboardingMigrationRun();
  const patchAmbiguity = usePatchPayComponentAmbiguity();

  /**
   * No dedicated `useOnboardingEntityRecordRead` hook exists (see
   * `src/onboarding/hooks/index.ts`), so this is a local, one-off mutation
   * wrapping the generated `getOnboardingEntityRecord` client directly —
   * the read-side counterpart to the Step 8 pattern's `patchMutation`
   * (there wrapping `patchOnboardingEntityRecord`). Modelled as a mutation
   * rather than a query because it is one step in an imperative pipeline
   * driven by the effect below, not an independently-cacheable read.
   */
  const readEntityRecord = useMutation({
    mutationFn: (args: { workloadId: string }) =>
      api.getOnboardingEntityRecord(args.workloadId, PAY_COMPONENTS_ENTITY_TYPE),
  });

  // ------------------------------------------------------------------
  // Driver effect — advances the import -> read -> optional PATCH ->
  // validate -> run pipeline one stage at a time. Each branch either kicks
  // off the next not-yet-started stage or returns early to wait for the
  // in-flight one to settle; the effect re-runs whenever any stage's status
  // changes.
  //
  // `invoke`/`mutate` calls are intentionally omitted from the dependency
  // array: `invoke` is a `useCallback([])`-wrapped stable reference (see
  // `useOnboardingImport.ts` et al.) and `useMutation`'s `mutate` is stable
  // across renders regardless of the inline `mutationFn` closure identity —
  // the same assumption `useWorkPatternImportWorkloadsManager` (Step 8)
  // already relies on.
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!isPipelineActive) return;

    if (workload.isError) {
      setIsPipelineActive(false);
      return;
    }
    const workloadId = workload.data?.workloadId;
    if (!workloadId) {
      // Workload query still resolving (create-or-reuse); wait for it.
      return;
    }

    // Stage 1: durable hold (multipart import).
    if (importState.status !== JobStatus.succeeded) {
      if (importState.status === JobStatus.failed || importState.error) {
        importKickoffInFlightRef.current = false;
        setIsPipelineActive(false);
        return;
      }
      if (!importState.isInvoking && !importState.importId) {
        const file = fileRef.current;
        if (!file) {
          setIsPipelineActive(false);
          setGuardError("No file to import. Please choose a CSV file first.");
          return;
        }
        if (importKickoffInFlightRef.current) {
          return;
        }
        importKickoffInFlightRef.current = true;
        // LADR-039 R-S1-1: re-import against an existing entity record must
        // echo `source_metadata.source_version` as `if_match_source_version`.
        const companyCodeForImport = companyCodeRef.current ?? undefined;
        const providerId = providerIdRef.current;
        void (async () => {
          try {
            const ifMatchSourceVersion = await resolveIfMatchSourceVersion(
              workloadId,
              PAY_COMPONENTS_ENTITY_TYPE,
            );
            importState.invoke({
              workloadId,
              file,
              entityType: PAY_COMPONENTS_ENTITY_TYPE,
              companyCode: companyCodeForImport,
              providerId,
              ifMatchSourceVersion,
            });
          } catch {
            importKickoffInFlightRef.current = false;
            setIsPipelineActive(false);
            setGuardError(
              "Could not check existing import state before uploading. Please try again.",
            );
          }
        })();
      }
      return;
    }

    // Stage 2: read back the held canonical rows. Needed both to decide
    // whether there is anything to stamp (stage 3) and, later, to join
    // validation/run row errors back to an employee/component (positional
    // lookup — see `payComponentWorkloadsResultMapper.ts`).
    if (!readEntityRecord.isSuccess) {
      if (readEntityRecord.isError) {
        setIsPipelineActive(false);
        return;
      }
      if (!readEntityRecord.isPending) {
        readEntityRecord.mutate({ workloadId });
      }
      return;
    }

    // Stage 3: conditionally stamp ambiguity resolutions (W3b). Skipped
    // entirely when the batch has no resolutions to apply — most imports
    // never hit an ambiguous component name.
    const resolutions = componentResolutionsRef.current;
    const hasResolutionsToStamp = Object.values(resolutions).some(
      (resolution) => !!resolution.companyComponentId,
    );
    if (hasResolutionsToStamp && !patchAmbiguity.isSuccess) {
      if (patchAmbiguity.isError) {
        setIsPipelineActive(false);
        return;
      }
      if (!patchAmbiguity.isPending) {
        patchAmbiguity.mutate({
          workloadId,
          heldRows: asHeldRows(readEntityRecord.data?.canonical_entities),
          resolutions,
        });
      }
      return;
    }

    // Stage 4: validate. `destinationDryRun: true` requests the Christmas
    // dry-run in addition to Pacific validation. This re-validates against
    // Christmas *after* ambiguity resolution (unlike the earlier W3c
    // preflight, which ran before Map Columns), so it can catch issues the
    // preflight could not have seen (e.g. an assignment-exists conflict on
    // the resolved `companyComponentId`). Gated server-side by
    // `ONBOARDING_MIGRATION_RUNS_ENABLED` — Api #1051 shipped this dark
    // (false in Sandbox/Preview/Production until a deliberate cutover) —
    // not re-checked client-side.
    if (validationState.status !== JobStatus.succeeded) {
      if (validationState.status === JobStatus.failed || validationState.error) {
        setIsPipelineActive(false);
        return;
      }
      if (!validationState.isInvoking && !validationState.validationId) {
        const batchMypayEnvironment = mypayEnvironmentRef.current;
        if (!batchMypayEnvironment) {
          setIsPipelineActive(false);
          setGuardError(
            "No MyPay environment selected. Please choose an environment before importing.",
          );
          return;
        }
        validationState.invoke({
          workloadId,
          entityType: PAY_COMPONENTS_ENTITY_TYPE,
          destinationDryRun: true,
          mypayEnvironment: batchMypayEnvironment,
          targetEnvironment: PAY_COMPONENTS_TARGET_ENVIRONMENT,
        });
      }
      return;
    }

    // A terminal `JobStatus.succeeded` validation *job* only means the job
    // ran to completion — the *report* may still carry blocking errors, in
    // which case the entity record transitioned to `validation_failed`, not
    // `validated`. Do not attempt to run against a hold that isn't
    // `validated`: stop here and let the derived `result` (status
    // "invalid") carry the errors table back to the caller.
    if ((validationState.data?.report?.errors?.length ?? 0) > 0) {
      setIsPipelineActive(false);
      return;
    }

    // Stage 5: run (skipped when D-2 already succeeded — re-import is validate-only).
    if (runPushAllowed === null) {
      if (runEligibilityCheckInFlightRef.current) {
        return;
      }
      runEligibilityCheckInFlightRef.current = true;
      void (async () => {
        try {
          const runs = await api.listOnboardingMigrationRuns(workloadId);
          const blocked = workloadHasSucceededPayComponentsRun(
            runs,
            PAY_COMPONENTS_TARGET_ENVIRONMENT,
          );
          setRunPushAllowed(!blocked);
        } catch {
          setGuardError(
            "Could not check prior import runs for this workload. Please try again.",
          );
          setIsPipelineActive(false);
        } finally {
          runEligibilityCheckInFlightRef.current = false;
        }
      })();
      return;
    }

    if (runPushAllowed === false) {
      setIsPipelineActive(false);
      return;
    }

    if (runState.status !== MigrationRunStatus.succeeded) {
      if (runState.status === MigrationRunStatus.failed || runState.error) {
        setIsPipelineActive(false);
        return;
      }
      if (!runState.isInvoking && !runState.runId) {
        runState.invoke({
          workloadId,
          targetEnvironment: PAY_COMPONENTS_TARGET_ENVIRONMENT,
          entityTypes: [PAY_COMPONENTS_ENTITY_TYPE],
          mypayEnvironment: mypayEnvironmentRef.current ?? undefined,
        });
      }
      return;
    }

    // All stages terminal-succeeded.
    setIsPipelineActive(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPipelineActive,
    workload.isError,
    workload.data?.workloadId,
    importState.status,
    importState.error,
    importState.isInvoking,
    importState.importId,
    readEntityRecord.isSuccess,
    readEntityRecord.isError,
    readEntityRecord.isPending,
    readEntityRecord.data,
    patchAmbiguity.isSuccess,
    patchAmbiguity.isError,
    patchAmbiguity.isPending,
    validationState.status,
    validationState.error,
    validationState.isInvoking,
    validationState.validationId,
    validationState.data,
    runState.status,
    runState.error,
    runState.isInvoking,
    runState.runId,
    runPushAllowed,
  ]);

  // ------------------------------------------------------------------
  // Derived result state
  // ------------------------------------------------------------------

  const heldRows = useMemo<HeldPayComponentRow[]>(() => {
    const source = patchAmbiguity.isSuccess
      ? patchAmbiguity.data?.canonical_entities
      : readEntityRecord.data?.canonical_entities;
    return asHeldRows(source);
  }, [
    patchAmbiguity.isSuccess,
    patchAmbiguity.data,
    readEntityRecord.data,
  ]);

  const validationResult = useMemo<ImportResult | null>(() => {
    if (validationState.status !== JobStatus.succeeded) return null;
    return mapValidationReportToImportResult(
      validationState.data?.report,
      heldRows,
    );
  }, [validationState.status, validationState.data, heldRows]);

  const commitResult = useMemo<ImportResult | null>(() => {
    if (!runState.data) return null;
    return mapRunViewToImportResult(runState.data, heldRows);
  }, [runState.data, heldRows]);

  const result = commitResult ?? validationResult;

  // Simplified error surfacing vs Track 1's `useWorkflowCommit` (which
  // classifies Temporal/agent-invocation-specific HTTP status codes): the
  // workloads REST API has a different error shape, so this derives one
  // generic message per stage instead, mirroring the Step 8 manager.
  const managerError = useMemo<string | null>(() => {
    if (guardError) return guardError;
    if (workload.isError) {
      return "Could not create or load the onboarding workload. Please try again.";
    }
    if (importState.error) {
      return `Import failed to start: ${importState.error}`;
    }
    if (importState.status === JobStatus.failed) {
      return (
        importState.data?.outcome_summary ??
        "The file import failed. Please try again."
      );
    }
    if (readEntityRecord.isError) {
      return "Could not read back the held import rows. Please try again.";
    }
    if (patchAmbiguity.isError) {
      return "Could not save your component resolutions before validating. Please try again.";
    }
    if (validationState.error) {
      return `Validation failed to start: ${validationState.error}`;
    }
    if (validationState.status === JobStatus.failed) {
      return "Validation failed. Please try again.";
    }
    if (runState.error) {
      return runState.error.includes("re-running after success")
        ? runState.error
        : `Import run failed to start: ${runState.error}`;
    }
    if (runState.status === MigrationRunStatus.failed) {
      return "The import run failed. Please try again.";
    }
    return null;
  }, [
    guardError,
    workload.isError,
    importState.error,
    importState.status,
    importState.data?.outcome_summary,
    readEntityRecord.isError,
    patchAmbiguity.isError,
    validationState.error,
    validationState.status,
    runState.error,
    runState.status,
  ]);

  // ------------------------------------------------------------------
  // Public interface
  // ------------------------------------------------------------------

  const commit = (args: PayComponentImportWorkloadsCommitArgs) => {
    companyCodeRef.current = args.companyCode;
    mypayEnvironmentRef.current = args.mypayEnvironment;
    providerIdRef.current = args.providerId;
    componentResolutionsRef.current = args.componentResolutions ?? {};
    fileRef.current = args.file;
    setCompanyCode(args.companyCode);

    if (!args.companyCode) {
      setGuardError(
        "No company code found. Please enter and validate a company code first.",
      );
      return;
    }
    if (!args.mypayEnvironment) {
      setGuardError(
        "No MyPay environment selected. Please choose an environment before importing.",
      );
      return;
    }
    if (!args.file) {
      setGuardError("No file to import. Please choose a CSV file first.");
      return;
    }

    setGuardError(null);
    importKickoffInFlightRef.current = false;
    runEligibilityCheckInFlightRef.current = false;
    setRunPushAllowed(null);
    importState.reset();
    readEntityRecord.reset();
    patchAmbiguity.reset();
    validationState.reset();
    runState.reset();
    setIsPipelineActive(true);
  };

  const reset = () => {
    companyCodeRef.current = null;
    mypayEnvironmentRef.current = null;
    providerIdRef.current = undefined;
    componentResolutionsRef.current = {};
    fileRef.current = null;
    importKickoffInFlightRef.current = false;
    runEligibilityCheckInFlightRef.current = false;
    setRunPushAllowed(null);
    setCompanyCode(null);
    setGuardError(null);
    setIsPipelineActive(false);
    importState.reset();
    readEntityRecord.reset();
    patchAmbiguity.reset();
    validationState.reset();
    runState.reset();
  };

  // The whole import->read->PATCH->validate->run pipeline is a single flag,
  // so "committing" is just "pipeline in flight" — same rationale as Step
  // 8's `isValidating`.
  const isCommitting = isPipelineActive;

  return {
    commit,
    result,
    isCommitting,
    managerError,
    reset,
  };
}
